import { useCallback, useMemo } from 'react'
import { removeFromArray, replaceInArray } from '.'
import { useLocalStorage } from './useLocalStorage'

const NEW_QUERY_ID = -1
const MAX_NUMBER_OF_STORED_QUERIES = 8
const EMPTY_QUERY = {
  combinator: 'and',
  rules: [],
}

const STORED_QUERIES = 'txnFilters'
const CURRENT_QUERY = 'txnFilters_idx'

export interface QueryBuilderPayload {
  currentQuery: any
  currentQueryRaw: string

  currentQueryIndex: number
  numberOfQueries: number
  numberOfRules: number

  isNewQuery: boolean

  canGoForward: boolean
  canGoBack: boolean

  clearFilter: Function
  deleteCurrentQuery: Function
  updateCurrentQuery: Function
  goToPreviousQuery: Function
  goToNextQuery: Function
  addRuleToQuery: Function
  removeRulesFromQuery: Function
}

export const useQueryFilter = (): QueryBuilderPayload => {
  const [rawStoredQueries, setRawStoredQueries] = useLocalStorage(
    STORED_QUERIES,
    []
  )

  const isValidStoredQuery = (query) =>
    query && Array.isArray(query.rules) && query.rules.length > 0

  const storedQueries = useMemo(
    () =>
      rawStoredQueries
        ?.map((q) => {
          try {
            return JSON.parse(q)
          } catch (err) {
            return null
          }
        })
        .filter((q) => q !== null),
    [rawStoredQueries]
  )

  const setStoredQueries = useCallback(
    (queries) =>
      setRawStoredQueries(
        queries.filter((q) => q !== null).map((q) => JSON.stringify(q))
      ),
    [setRawStoredQueries]
  )

  const [rawCurrentQueryIndex, setCurrentQueryIndex] = useLocalStorage(
    CURRENT_QUERY,
    NEW_QUERY_ID
  )
  const currentQueryIndex = useMemo(
    () => Number(rawCurrentQueryIndex),
    [rawCurrentQueryIndex]
  )

  const isNewQuery = useMemo(
    () => currentQueryIndex === NEW_QUERY_ID,
    [currentQueryIndex]
  )

  // Instead of filtering by isValidStoredQuery, allow empty queries
  // This fixes the issue where queries get lost during state updates
  const currentQuery = useMemo(() => {
    const query = storedQueries[currentQueryIndex]
    console.log('currentQuery computed:', {
      currentQueryIndex,
      storedQueriesLength: storedQueries.length,
      query,
      returning:
        currentQueryIndex === NEW_QUERY_ID || !query ? 'EMPTY_QUERY' : query,
    })
    if (currentQueryIndex === NEW_QUERY_ID || !query) return EMPTY_QUERY
    return query
  }, [currentQueryIndex, storedQueries])

  const currentQueryRaw = useMemo(
    () => JSON.stringify(currentQuery),
    [currentQuery]
  )

  const numberOfQueries = useMemo(() => storedQueries.length, [storedQueries])
  const numberOfRules = useMemo(() => currentQuery.rules.length, [currentQuery])

  const canGoBack = useMemo(() => currentQueryIndex > 0, [currentQueryIndex])
  const canGoForward = useMemo(
    () => currentQueryIndex < storedQueries.length - 1,
    [currentQueryIndex, storedQueries]
  )
  const goToPreviousQuery = useCallback(() => {
    const nextQuery = canGoBack
      ? currentQueryIndex - 1
      : storedQueries.length - 1
    setCurrentQueryIndex(nextQuery)
  }, [canGoBack, currentQueryIndex, setCurrentQueryIndex, storedQueries.length])

  const goToNextQuery = useCallback(() => {
    const nextQuery = canGoForward
      ? currentQueryIndex + 1
      : storedQueries.length - 1
    setCurrentQueryIndex(nextQuery)
  }, [
    canGoForward,
    currentQueryIndex,
    setCurrentQueryIndex,
    storedQueries.length,
  ])

  const deleteCurrentQuery = () => {
    console.log('deleteCurrentQuery called')
    setStoredQueries(removeFromArray(storedQueries, currentQueryIndex))
    setCurrentQueryIndex(NEW_QUERY_ID)
  }

  const updateCurrentQuery = (query) => {
    const formattedQuery = JSON.parse(query)
    console.log('updateCurrentQuery:', {
      isNewQuery,
      formattedQuery,
      currentQueryIndex,
      storedQueries: storedQueries.length,
    })
    if (isNewQuery) {
      const result = [
        ...storedQueries.slice(-MAX_NUMBER_OF_STORED_QUERIES),
        formattedQuery,
      ]
      const cleaned = result.filter(isValidStoredQuery)
      console.log('Storing new query:', {
        result,
        cleaned,
        newIndex: cleaned.length - 1,
      })
      setStoredQueries(cleaned)
      console.log('Setting currentQueryIndex to:', cleaned.length - 1)
      setCurrentQueryIndex(cleaned.length - 1)
    } else {
      const updated = replaceInArray(
        storedQueries,
        currentQueryIndex,
        formattedQuery
      )
      console.log('Updating existing query:', { updated })
      setStoredQueries(updated)
    }
  }

  const addRuleToQuery = (field, value) => {
    // Remove any existing rule with the same field to avoid duplicates
    const filteredRules = currentQuery.rules.filter((r) => r.field !== field)

    console.log('addRuleToQuery:', {
      field,
      value,
      existingRules: currentQuery.rules,
      filteredRules,
      newRule: { field, value, operator: 'in', valueSource: 'value' },
    })

    updateCurrentQuery(
      JSON.stringify({
        ...currentQuery,
        not: false,
        rules: [
          ...filteredRules,
          { field, value, operator: 'in', valueSource: 'value' },
        ],
      })
    )
  }

  const removeRulesFromQuery = (indices: number[]) => {
    updateCurrentQuery(
      JSON.stringify({
        ...currentQuery,
        rules: currentQuery.rules.filter((_, i) => !indices.includes(i)),
      })
    )
  }

  const clearFilter = () => {
    console.log('clearFilter called - setting index to NEW_QUERY_ID')
    console.trace('clearFilter stack trace')
    setCurrentQueryIndex(NEW_QUERY_ID)
  }

  return {
    currentQueryIndex,
    currentQuery,
    currentQueryRaw,
    numberOfRules,
    numberOfQueries,
    canGoBack,
    canGoForward,
    goToPreviousQuery,
    goToNextQuery,
    deleteCurrentQuery,
    updateCurrentQuery,
    clearFilter,
    addRuleToQuery,
    removeRulesFromQuery,
    isNewQuery,
  }
}
